/**
 * Chart builder registry. Each ChartVizSpec type maps to a pure
 * `(ctx) => EChartsCoreOption` builder living in its own module. `buildOption`
 * computes the shared key/measure locals once, then dispatches. Types absent
 * from the table (e.g. a future viz before its builder lands) fall back to the
 * bare base option rather than throwing.
 */
import type { ChartVizSpec, QuerySpec } from "@/lib/reports/schema";
import type { QueryResult } from "@/lib/reports/adapters/types";

import { FLOATS_PALETTE } from "./palette";
import { buildBaseOption } from "./context";
import type { ChartBuildCtx, ChartBuilder, ChartOption } from "./context";
import { buildLineAreaOption } from "./line-area";
import { buildBarOption } from "./bar";
import { buildHeatmapOption } from "./heatmap";
import { buildSankeyOption } from "./sankey";
import { buildCohortOption } from "./cohort";
import { buildWaterfallOption } from "./waterfall";
import { buildRadarOption } from "./radar";
import { buildBoxplotOption } from "./boxplot";
import { buildPieDonutOption } from "./pie-donut";
import { buildScatterOption } from "./scatter";
import { buildFunnelOption } from "./funnel";
import { buildTreemapOption } from "./treemap";
import { buildGaugeOption } from "./gauge";
import { buildCalendarHeatmapOption } from "./calendar-heatmap";

const CHART_BUILDERS: Partial<Record<ChartVizSpec["type"], ChartBuilder>> = {
  line: buildLineAreaOption,
  area: buildLineAreaOption,
  bar: buildBarOption,
  stacked_bar: buildBarOption,
  horizontal_bar: buildBarOption,
  scatter: buildScatterOption,
  heatmap: buildHeatmapOption,
  funnel: buildFunnelOption,
  cohort: buildCohortOption,
  sankey: buildSankeyOption,
  treemap: buildTreemapOption,
  waterfall: buildWaterfallOption,
  gauge: buildGaugeOption,
  radar: buildRadarOption,
  calendar_heatmap: buildCalendarHeatmapOption,
  boxplot: buildBoxplotOption,
  pie: buildPieDonutOption,
  donut: buildPieDonutOption,
};

/** All chart viz types that have a registered builder, in display order. */
export const SUPPORTED_CHART_TYPES = Object.keys(CHART_BUILDERS) as ChartVizSpec["type"][];

export function buildOption(
  viz: ChartVizSpec,
  query: QuerySpec,
  data: QueryResult,
  palette: string[] = FLOATS_PALETTE,
): ChartOption {
  const measure = query.measures[0];
  const dimension = query.dimensions?.[0];
  const time = query.timeDimension?.member;
  const xKey = viz.encodings.x ?? dimension ?? time ?? "x";
  const yKey = viz.encodings.y ?? measure ?? "y";

  const base = buildBaseOption(palette);
  const ctx: ChartBuildCtx = { viz, query, data, palette, base, measure, dimension, time, xKey, yKey };

  const builder = CHART_BUILDERS[viz.type];
  return builder ? builder(ctx) : base;
}
